import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {createSuggestionsCache, type SearchComboboxStatus, useSuggestionsQuery} from "@pg-design/search-combobox-module";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {fetchPricesRegionSuggestions} from "../api/fetch_prices_region_suggestions";
import {getPricesAddressSearchCacheKey, pricesAddressSearchCache, rcnDirectAddressSource} from "../api/prices_address_source";
import {
    PRICES_SEARCH_CACHE_MAX,
    PRICES_SEARCH_CACHE_TTL_MS,
    PRICES_SEARCH_DEBOUNCE_MS,
    PRICES_SEARCH_GROUP_LIMIT,
    PRICES_SEARCH_MIN_QUERY_LENGTH,
    PRICES_SEARCH_NUMBERS_PREVIEW,
    RCN_ADDRESSES_MAX_LIMIT
} from "../constants/prices_search";
import type {
    IParsedPricesSearchQuery,
    IPricesAddressSearchResult,
    IPricesAddressSource,
    IPricesRecentSearch,
    IPricesRegionSuggestionsResult,
    IPricesSearchContext,
    IPricesSearchModel,
    IPricesSearchNearbyState,
    IPricesSearchShowMoreItem,
    IPricesSearchStreetItem,
    IPricesSearchUiState,
    PricesSearchSectionKind
} from "../types/IPricesSearchModel";
import {
    applyPricesSearchNumberPrices,
    buildPricesSearchModel,
    getPricesSearchDistricts,
    getPricesSearchPricedAddresses
} from "../utils/prices_search/build_prices_search_model";
import {getCityScopedQuery, getShowMoreCitySlugs, groupRcnAddresses} from "../utils/prices_search/group_rcn_addresses";
import {parsePricesSearchQuery} from "../utils/prices_search/parse_prices_search_query";
import {usePricesSearchCityMetrics} from "./use_prices_search_city_metrics";
import {getPricesSearchNumberPriceKey, usePricesSearchNumberPrices} from "./use_prices_search_number_prices";
import {usePricesSearchShowMore} from "./use_prices_search_show_more";

const EMPTY_ADDRESSES: IPricesAddressSearchResult = {rows: [], capped: false};
const EMPTY_REGIONS: IPricesRegionSuggestionsResult = {districts: [], citiesWithoutData: []};
const PRICES_CITY_NAME_BY_SLUG = new Map<string, string>(PRICES_CITIES.map((city) => [city.slug, city.name]));

/** Region suggestions per sanitized query; the address answers share `pricesAddressSearchCache` with the scoped requests. */
export const pricesRegionSuggestionsCache = createSuggestionsCache<IPricesRegionSuggestionsResult>({
    max: PRICES_SEARCH_CACHE_MAX,
    ttlMs: PRICES_SEARCH_CACHE_TTL_MS
});

const getRcnQuery = (query: string): string => parsePricesSearchQuery(query).rcnQuery;

const isPending = (status: SearchComboboxStatus): boolean => status === "debouncing" || status === "loading";
const isSettled = (status: SearchComboboxStatus): boolean => status === "success" || status === "empty";

/** What the list makes active after an expansion: the first row that was hidden until now. */
type ActiveRequest = {baseKey: string} & (
    | {type: "section"; kind: PricesSearchSectionKind; index: number}
    | {type: "numbers"; streetKey: string; index: number}
);

interface IUiState extends Omit<IPricesSearchUiState, "loadingShowMoreKeys"> {
    /** The phrase the state belongs to - a new phrase starts over. */
    baseKey: string | null;
}

const INITIAL_UI: Omit<IUiState, "baseKey"> = {expandedStreetKey: undefined, expandedSections: [], expandedNumberStreetKeys: []};

const findStreet = (model: IPricesSearchModel, streetKey: string): IPricesSearchStreetItem | undefined => {
    for (const section of model.sections) {
        for (const item of section.items) {
            if (item.kind === "street" && item.streetKey === streetKey) {
                return item;
            }
        }
    }

    return undefined;
};

const resolveActiveRequest = (model: IPricesSearchModel, request: ActiveRequest): string | null => {
    if (request.type === "numbers") {
        return findStreet(model, request.streetKey)?.numbers?.items[request.index]?.id ?? null;
    }

    return model.sections.find((section) => section.kind === request.kind)?.items[request.index]?.id ?? null;
};

export interface IUsePricesSearchSuggestionsOptions {
    /** Raw field value. */
    query: string;
    /** Requests run only while the panel is open; closing cancels what is in flight. */
    isOpen: boolean;
    context: IPricesSearchContext;
    /** `usePricesSearchRecentSearches().items`. */
    recent: readonly IPricesRecentSearch[];
    nearby: IPricesSearchNearbyState;
    /** Enter pressed while the phrase was loading: its results arrived - select `itemId` (the model's `autoActiveId`). */
    onPendingSubmitReady: (itemId: string) => void;
    /** Default: RCN straight from the browser. */
    source?: IPricesAddressSource;
}

export interface IUsePricesSearchSuggestionsResult {
    /** With the lazy prices of the rendered numbers and "Adresy" rows applied (unavailable ones flagged). */
    model: IPricesSearchModel;
    parsed: IParsedPricesSearchQuery | null;
    /** A request for the phrase is still on its way (address or region query). */
    isPending: boolean;
    /** The field spinner, in place of ×: the phrase is loading - the same moments the dropdown shows the skeleton. */
    isBusy: boolean;
    /** Street row selected: expands it (only one at a time) or collapses it. */
    toggleStreet: (street: IPricesSearchStreetItem) => void;
    /** "Pokaż więcej …" / "Pokaż wszystkie numery" selected. */
    showMore: (item: IPricesSearchShowMoreItem) => void;
    /** After an expansion: the first newly shown row, to make active (and scroll to). Call `consumeActiveRequest` once used. */
    requestedActiveId: string | null;
    consumeActiveRequest: () => void;
    /** "Spróbuj ponownie" and Enter in the error state. */
    retry: () => void;
    /** × in the field: cancels every request of the phrase. */
    abortAll: () => void;
    /** Enter while the phrase is loading: picks the first result once it arrives, unless the phrase changes first. */
    markPendingSubmit: () => void;
    /** The auto-active row is a number or address whose price is still loading; Enter waits for it. */
    isAutoActiveResolving: boolean;
}

/**
 * Everything between the typed phrase and the rendered list. Parses the phrase, runs the address
 * and the region queries (debounced, cached, cancelled on change and on close), adds the scoped "Pokaż więcej" and
 * building-number answers, the metrics and the recent searches, and builds the view model. `baseKey` is the single
 * source of truth: the UI state, the scoped answers and a pending Enter all belong to one phrase and are dropped with it.
 */
export const usePricesSearchSuggestions = (options: IUsePricesSearchSuggestionsOptions): IUsePricesSearchSuggestionsResult => {
    const {query, isOpen, context, recent, nearby, source = rcnDirectAddressSource} = options;

    const parsed = useMemo(() => (query.trim().length >= PRICES_SEARCH_MIN_QUERY_LENGTH ? parsePricesSearchQuery(query) : null), [query]);
    const baseKey = parsed?.baseKey ?? null;

    const addresses = useSuggestionsQuery<IPricesAddressSearchResult>({
        query,
        isEnabled: isOpen,
        minLength: PRICES_SEARCH_MIN_QUERY_LENGTH,
        debounceMs: PRICES_SEARCH_DEBOUNCE_MS,
        cache: pricesAddressSearchCache,
        // Phrases that sanitize to the same RCN query ("lodz", "Łódź ") share one request.
        getCacheKey: (trimmed) => getPricesAddressSearchCacheKey(source, getRcnQuery(trimmed), RCN_ADDRESSES_MAX_LIMIT),
        isEmpty: (result) => result.rows.length === 0,
        fetcher: (trimmed, {signal}) => {
            const rcnQuery = getRcnQuery(trimmed);

            // Only abbreviations or street-type words ("ul.") - nothing to ask RCN for; the cities still match locally.
            return rcnQuery ? source.search(rcnQuery, {limit: RCN_ADDRESSES_MAX_LIMIT, signal}) : Promise.resolve(EMPTY_ADDRESSES);
        }
    });

    const regions = useSuggestionsQuery<IPricesRegionSuggestionsResult>({
        query,
        isEnabled: isOpen,
        minLength: PRICES_SEARCH_MIN_QUERY_LENGTH,
        debounceMs: PRICES_SEARCH_DEBOUNCE_MS,
        cache: pricesRegionSuggestionsCache,
        getCacheKey: getRcnQuery,
        fetcher: (trimmed, {signal}) => {
            const rcnQuery = getRcnQuery(trimmed);

            return rcnQuery ? fetchPricesRegionSuggestions(rcnQuery, {signal}) : Promise.resolve(EMPTY_REGIONS);
        }
    });

    const showMoreRequests = usePricesSearchShowMore({baseKey, source});

    // UI state of one phrase: typing something else collapses what the user expanded.
    const [uiState, setUiState] = useState<IUiState>({baseKey, ...INITIAL_UI});
    const ui = uiState.baseKey === baseKey ? uiState : {baseKey, ...INITIAL_UI};
    const updateUi = useCallback(
        (update: (previous: Omit<IUiState, "baseKey">) => Omit<IUiState, "baseKey">) =>
            setUiState((previous) => ({baseKey, ...update(previous.baseKey === baseKey ? previous : INITIAL_UI)})),
        [baseKey]
    );

    const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
    const [pendingSubmitKey, setPendingSubmitKey] = useState<string | null>(null);

    // A region failure is silent (the districts group just does not show), so only the addresses decide the error state.
    const isAddressesSettled = isSettled(addresses.status);
    const isRegionsPending = isPending(regions.status);
    const status: SearchComboboxStatus = addresses.status !== "error" && isAddressesSettled && isRegionsPending ? "loading" : addresses.status;
    const base = isAddressesSettled ? addresses.data : null;

    // A "Pokaż więcej" that ended with nothing (failed, or cancelled by closing the panel) collapses back to its link, so
    // clicking it again retries; a new request for it clears the failure.
    const {failedKeys} = showMoreRequests;
    const expandedSections = useMemo(() => ui.expandedSections.filter((kind) => !failedKeys.includes(kind)), [ui.expandedSections, failedKeys]);
    const expandedNumberStreetKeys = useMemo(
        () => ui.expandedNumberStreetKeys.filter((streetKey) => !failedKeys.includes(streetKey)),
        [ui.expandedNumberStreetKeys, failedKeys]
    );

    const isDistrictsExpanded = expandedSections.includes("districts");
    const regionsResult = (isDistrictsExpanded ? showMoreRequests.expandedRegions : null) ?? regions.data;

    // Metrics are asked for the districts the list shows, which does not depend on the metrics themselves.
    const districtRegionIds = useMemo(() => {
        if (!parsed) {
            return [];
        }

        const districts = getPricesSearchDistricts(parsed, regionsResult, context);

        return (isDistrictsExpanded ? districts : districts.slice(0, PRICES_SEARCH_GROUP_LIMIT)).map((district) => district.regionId);
    }, [parsed, regionsResult, context, isDistrictsExpanded]);
    const metrics = usePricesSearchCityMetrics({isOpen, districtRegionIds});

    const {expandedStreetKey} = ui;
    const loadingShowMoreKeys = showMoreRequests.loadingKeys;
    const scoped = showMoreRequests.scoped;
    const baseModel = useMemo(
        () =>
            buildPricesSearchModel({
                parsed,
                status,
                base,
                scoped,
                regions: regionsResult,
                metrics,
                recent,
                nearby,
                ui: {expandedStreetKey, expandedSections, expandedNumberStreetKeys, loadingShowMoreKeys},
                context
            }),
        [
            parsed,
            status,
            base,
            scoped,
            regionsResult,
            metrics,
            recent,
            nearby,
            expandedStreetKey,
            expandedSections,
            expandedNumberStreetKeys,
            loadingShowMoreKeys,
            context
        ]
    );

    // Which rows are rendered does not depend on their prices, so the base model decides what to fetch.
    const pricedAddresses = useMemo(() => getPricesSearchPricedAddresses(baseModel), [baseModel]);
    const numberPrices = usePricesSearchNumberPrices(pricedAddresses);
    const model = useMemo(() => applyPricesSearchNumberPrices(baseModel, numberPrices), [baseModel, numberPrices]);

    // A number or an address still waiting for its price may turn out unavailable - autoActiveId then moves past it.
    const autoActiveItem =
        model.state === "results" && model.autoActiveId !== null ? model.navigation.find((item) => item.id === model.autoActiveId) : undefined;
    const isAutoActiveResolving =
        (autoActiveItem?.kind === "number" || autoActiveItem?.kind === "address") &&
        numberPrices[getPricesSearchNumberPriceKey(autoActiveItem.rcn)]?.isLoading === true;

    // The expanded street of a real (not stale) answer gets all its numbers when the answer was capped.
    const {loadStreetNumbers, loadCityStreets, loadDistricts, abortAll: abortShowMore} = showMoreRequests;
    const incompleteExpandedStreet = isAddressesSettled
        ? model.sections
              .flatMap((section) => section.items)
              .find((item): item is IPricesSearchStreetItem => item.kind === "street" && item.numbers?.isTotalExact === false)
        : undefined;
    useEffect(() => {
        if (incompleteExpandedStreet) {
            loadStreetNumbers(incompleteExpandedStreet, {silent: true});
        }
    }, [incompleteExpandedStreet?.streetKey, loadStreetNumbers]);

    // Closing the panel cancels the scoped requests too (the main ones stop through `isEnabled`), and a pending Enter.
    // The row an expansion was going to make active is forgotten too: reopening must not jump to it.
    useEffect(() => {
        if (!isOpen) {
            abortShowMore();
            setPendingSubmitKey(null);
            setActiveRequest(null);
        }
    }, [isOpen, abortShowMore]);

    const onPendingSubmitReadyRef = useRef(options.onPendingSubmitReady);
    useEffect(() => {
        onPendingSubmitReadyRef.current = options.onPendingSubmitReady;
    });

    // Enter during loading waits for this phrase only; a new phrase, an error or no results drop it.
    const isQuerySettled = isAddressesSettled && !isRegionsPending;
    useEffect(() => {
        if (pendingSubmitKey === null) {
            return;
        }
        if (pendingSubmitKey !== baseKey || addresses.status === "error") {
            setPendingSubmitKey(null);
            return;
        }
        // The auto-active row's price is still on its way: wait for it - the row may turn out unavailable.
        if (!isQuerySettled || isAutoActiveResolving) {
            return;
        }

        setPendingSubmitKey(null);
        if (model.state === "results" && model.autoActiveId !== null) {
            onPendingSubmitReadyRef.current(model.autoActiveId);
        }
    }, [pendingSubmitKey, baseKey, addresses.status, isQuerySettled, isAutoActiveResolving, model]);

    const validActiveRequest = activeRequest && activeRequest.baseKey === baseKey ? activeRequest : null;
    const requestedActiveId = validActiveRequest ? resolveActiveRequest(model, validActiveRequest) : null;
    // Nothing new appeared once the requests are done (e.g. a capped street had no more numbers): forget the request.
    useEffect(() => {
        if (activeRequest && (activeRequest.baseKey !== baseKey || (requestedActiveId === null && loadingShowMoreKeys.length === 0 && isQuerySettled))) {
            setActiveRequest(null);
        }
    }, [activeRequest, baseKey, requestedActiveId, loadingShowMoreKeys, isQuerySettled]);

    const consumeActiveRequest = useCallback(() => setActiveRequest(null), []);

    const toggleStreet = useCallback(
        (street: IPricesSearchStreetItem) => {
            if (baseKey === null) {
                return;
            }

            updateUi((previous) => ({...previous, expandedStreetKey: street.isExpanded ? null : street.streetKey}));
            setActiveRequest(street.isExpanded ? null : {baseKey, type: "numbers", streetKey: street.streetKey, index: 0});
        },
        [baseKey, updateUi]
    );

    const showMore = useCallback(
        (item: IPricesSearchShowMoreItem) => {
            if (parsed === null) {
                return;
            }

            if (item.target === "numbers") {
                const street = item.streetKey === undefined ? undefined : findStreet(model, item.streetKey);
                if (!street) {
                    return;
                }

                // A retry after a failure finds the key already there.
                updateUi((previous) =>
                    previous.expandedNumberStreetKeys.includes(street.streetKey)
                        ? previous
                        : {...previous, expandedNumberStreetKeys: [...previous.expandedNumberStreetKeys, street.streetKey]}
                );
                setActiveRequest({baseKey: parsed.baseKey, type: "numbers", streetKey: street.streetKey, index: PRICES_SEARCH_NUMBERS_PREVIEW});
                if (street.numbers?.isTotalExact === false) {
                    loadStreetNumbers(street);
                }
                return;
            }

            const kind = item.target;
            const shownCount = model.sections.find((section) => section.kind === kind)?.items.length ?? 0;
            updateUi((previous) =>
                previous.expandedSections.includes(kind) ? previous : {...previous, expandedSections: [...previous.expandedSections, kind]}
            );
            setActiveRequest({baseKey: parsed.baseKey, type: "section", kind, index: shownCount});

            if (kind === "districts") {
                loadDistricts(parsed.rcnQuery);
            }
            // A capped answer may hide more streets: ask again per city (the page's city, else the busiest ones).
            if ((kind === "streets" || kind === "addresses") && base?.capped) {
                const grouped = groupRcnAddresses({parsed, base, scoped, context});
                const cities = getShowMoreCitySlugs(grouped.streets, context).map((citySlug) => ({
                    citySlug,
                    query: PRICES_CITY_NAME_BY_SLUG.has(citySlug) ? getCityScopedQuery(parsed, PRICES_CITY_NAME_BY_SLUG.get(citySlug) as string) : null
                }));
                loadCityStreets(kind, cities);
            }
        },
        [parsed, model, base, scoped, context, updateUi, loadStreetNumbers, loadDistricts, loadCityStreets]
    );

    const {retry: retryAddresses, abort: abortAddresses} = addresses;
    const {retry: retryRegions, abort: abortRegions} = regions;
    const regionsStatus = regions.status;
    const retry = useCallback(() => {
        retryAddresses();
        if (regionsStatus === "error") {
            retryRegions();
        }
    }, [retryAddresses, retryRegions, regionsStatus]);

    const abortAll = useCallback(() => {
        abortAddresses();
        abortRegions();
        abortShowMore();
        setPendingSubmitKey(null);
    }, [abortAddresses, abortRegions, abortShowMore]);

    const markPendingSubmit = useCallback(() => setPendingSubmitKey(baseKey), [baseKey]);

    return {
        model,
        parsed,
        isPending: isPending(addresses.status) || isRegionsPending,
        isBusy: isPending(status),
        toggleStreet,
        showMore,
        requestedActiveId,
        consumeActiveRequest,
        retry,
        abortAll,
        markPendingSubmit,
        isAutoActiveResolving
    };
};
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {createSuggestionsCache, type SearchComboboxStatus, useSuggestionsQuery} from "@pg-design/search-combobox-module";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {fetchPricesRegionSuggestions} from "../api/fetch_prices_region_suggestions";
import {getPricesAddressSearchCacheKey, pricesAddressSearchCache, rcnDirectAddressSource} from "../api/prices_address_source";
import {
    PRICES_SEARCH_CACHE_MAX,
    PRICES_SEARCH_CACHE_TTL_MS,
    PRICES_SEARCH_DEBOUNCE_MS,
    PRICES_SEARCH_GROUP_LIMIT,
    PRICES_SEARCH_MIN_QUERY_LENGTH,
    PRICES_SEARCH_NUMBERS_PREVIEW,
    RCN_ADDRESSES_MAX_LIMIT
} from "../constants/prices_search";
import type {
    IParsedPricesSearchQuery,
    IPricesAddressSearchResult,
    IPricesAddressSource,
    IPricesRecentSearch,
    IPricesRegionSuggestionsResult,
    IPricesSearchContext,
    IPricesSearchModel,
    IPricesSearchNearbyState,
    IPricesSearchShowMoreItem,
    IPricesSearchStreetItem,
    IPricesSearchUiState,
    PricesSearchSectionKind
} from "../types/IPricesSearchModel";
import {
    applyPricesSearchNumberPrices,
    buildPricesSearchModel,
    getPricesSearchDistricts,
    getPricesSearchPricedAddresses
} from "../utils/prices_search/build_prices_search_model";
import {getCityScopedQuery, getShowMoreCitySlugs, groupRcnAddresses} from "../utils/prices_search/group_rcn_addresses";
import {parsePricesSearchQuery} from "../utils/prices_search/parse_prices_search_query";
import {usePricesSearchCityMetrics} from "./use_prices_search_city_metrics";
import {getPricesSearchNumberPriceKey, usePricesSearchNumberPrices} from "./use_prices_search_number_prices";
import {usePricesSearchShowMore} from "./use_prices_search_show_more";

const EMPTY_ADDRESSES: IPricesAddressSearchResult = {rows: [], capped: false};
const EMPTY_REGIONS: IPricesRegionSuggestionsResult = {districts: [], citiesWithoutData: []};
const PRICES_CITY_NAME_BY_SLUG = new Map<string, string>(PRICES_CITIES.map((city) => [city.slug, city.name]));

/** Region suggestions per sanitized query; the address answers share `pricesAddressSearchCache` with the scoped requests. */
export const pricesRegionSuggestionsCache = createSuggestionsCache<IPricesRegionSuggestionsResult>({
    max: PRICES_SEARCH_CACHE_MAX,
    ttlMs: PRICES_SEARCH_CACHE_TTL_MS
});

const getRcnQuery = (query: string): string => parsePricesSearchQuery(query).rcnQuery;

const isPending = (status: SearchComboboxStatus): boolean => status === "debouncing" || status === "loading";
const isSettled = (status: SearchComboboxStatus): boolean => status === "success" || status === "empty";

/** What the list makes active after an expansion: the first row that was hidden until now. */
type ActiveRequest = {baseKey: string} & (
    | {type: "section"; kind: PricesSearchSectionKind; index: number}
    | {type: "numbers"; streetKey: string; index: number}
);

interface IUiState extends Omit<IPricesSearchUiState, "loadingShowMoreKeys"> {
    /** The phrase the state belongs to - a new phrase starts over. */
    baseKey: string | null;
}

const INITIAL_UI: Omit<IUiState, "baseKey"> = {expandedStreetKey: undefined, expandedSections: [], expandedNumberStreetKeys: []};

const findStreet = (model: IPricesSearchModel, streetKey: string): IPricesSearchStreetItem | undefined => {
    for (const section of model.sections) {
        for (const item of section.items) {
            if (item.kind === "street" && item.streetKey === streetKey) {
                return item;
            }
        }
    }

    return undefined;
};

const resolveActiveRequest = (model: IPricesSearchModel, request: ActiveRequest): string | null => {
    if (request.type === "numbers") {
        return findStreet(model, request.streetKey)?.numbers?.items[request.index]?.id ?? null;
    }

    return model.sections.find((section) => section.kind === request.kind)?.items[request.index]?.id ?? null;
};

export interface IUsePricesSearchSuggestionsOptions {
    /** Raw field value. */
    query: string;
    /** Requests run only while the panel is open; closing cancels what is in flight. */
    isOpen: boolean;
    context: IPricesSearchContext;
    /** `usePricesSearchRecentSearches().items`. */
    recent: readonly IPricesRecentSearch[];
    nearby: IPricesSearchNearbyState;
    /** Enter pressed while the phrase was loading: its results arrived - select `itemId` (the model's `autoActiveId`). */
    onPendingSubmitReady: (itemId: string) => void;
    /** Default: RCN straight from the browser. */
    source?: IPricesAddressSource;
}

export interface IUsePricesSearchSuggestionsResult {
    /** With the lazy prices of the rendered numbers and "Adresy" rows applied (unavailable ones flagged). */
    model: IPricesSearchModel;
    parsed: IParsedPricesSearchQuery | null;
    /** A request for the phrase is still on its way (address or region query). */
    isPending: boolean;
    /** The field spinner, in place of ×: the phrase is loading - the same moments the dropdown shows the skeleton. */
    isBusy: boolean;
    /** Street row selected: expands it (only one at a time) or collapses it. */
    toggleStreet: (street: IPricesSearchStreetItem) => void;
    /** "Pokaż więcej …" / "Pokaż wszystkie numery" selected. */
    showMore: (item: IPricesSearchShowMoreItem) => void;
    /** After an expansion: the first newly shown row, to make active (and scroll to). Call `consumeActiveRequest` once used. */
    requestedActiveId: string | null;
    consumeActiveRequest: () => void;
    /** "Spróbuj ponownie" and Enter in the error state. */
    retry: () => void;
    /** × in the field: cancels every request of the phrase. */
    abortAll: () => void;
    /** Enter while the phrase is loading: picks the first result once it arrives, unless the phrase changes first. */
    markPendingSubmit: () => void;
    /** The auto-active row is a number or address whose price is still loading; Enter waits for it. */
    isAutoActiveResolving: boolean;
}

/**
 * Everything between the typed phrase and the rendered list. Parses the phrase, runs the address
 * and the region queries (debounced, cached, cancelled on change and on close), adds the scoped "Pokaż więcej" and
 * building-number answers, the metrics and the recent searches, and builds the view model. `baseKey` is the single
 * source of truth: the UI state, the scoped answers and a pending Enter all belong to one phrase and are dropped with it.
 */
export const usePricesSearchSuggestions = (options: IUsePricesSearchSuggestionsOptions): IUsePricesSearchSuggestionsResult => {
    const {query, isOpen, context, recent, nearby, source = rcnDirectAddressSource} = options;

    const parsed = useMemo(() => (query.trim().length >= PRICES_SEARCH_MIN_QUERY_LENGTH ? parsePricesSearchQuery(query) : null), [query]);
    const baseKey = parsed?.baseKey ?? null;

    const addresses = useSuggestionsQuery<IPricesAddressSearchResult>({
        query,
        isEnabled: isOpen,
        minLength: PRICES_SEARCH_MIN_QUERY_LENGTH,
        debounceMs: PRICES_SEARCH_DEBOUNCE_MS,
        cache: pricesAddressSearchCache,
        // Phrases that sanitize to the same RCN query ("lodz", "Łódź ") share one request.
        getCacheKey: (trimmed) => getPricesAddressSearchCacheKey(source, getRcnQuery(trimmed), RCN_ADDRESSES_MAX_LIMIT),
        isEmpty: (result) => result.rows.length === 0,
        fetcher: (trimmed, {signal}) => {
            const rcnQuery = getRcnQuery(trimmed);

            // Only abbreviations or street-type words ("ul.") - nothing to ask RCN for; the cities still match locally.
            return rcnQuery ? source.search(rcnQuery, {limit: RCN_ADDRESSES_MAX_LIMIT, signal}) : Promise.resolve(EMPTY_ADDRESSES);
        }
    });

    const regions = useSuggestionsQuery<IPricesRegionSuggestionsResult>({
        query,
        isEnabled: isOpen,
        minLength: PRICES_SEARCH_MIN_QUERY_LENGTH,
        debounceMs: PRICES_SEARCH_DEBOUNCE_MS,
        cache: pricesRegionSuggestionsCache,
        getCacheKey: getRcnQuery,
        fetcher: (trimmed, {signal}) => {
            const rcnQuery = getRcnQuery(trimmed);

            return rcnQuery ? fetchPricesRegionSuggestions(rcnQuery, {signal}) : Promise.resolve(EMPTY_REGIONS);
        }
    });

    const showMoreRequests = usePricesSearchShowMore({baseKey, source});

    // UI state of one phrase: typing something else collapses what the user expanded.
    const [uiState, setUiState] = useState<IUiState>({baseKey, ...INITIAL_UI});
    const ui = uiState.baseKey === baseKey ? uiState : {baseKey, ...INITIAL_UI};
    const updateUi = useCallback(
        (update: (previous: Omit<IUiState, "baseKey">) => Omit<IUiState, "baseKey">) =>
            setUiState((previous) => ({baseKey, ...update(previous.baseKey === baseKey ? previous : INITIAL_UI)})),
        [baseKey]
    );

    const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
    const [pendingSubmitKey, setPendingSubmitKey] = useState<string | null>(null);

    // A region failure is silent (the districts group just does not show), so only the addresses decide the error state.
    const isAddressesSettled = isSettled(addresses.status);
    const isRegionsPending = isPending(regions.status);
    const status: SearchComboboxStatus = addresses.status !== "error" && isAddressesSettled && isRegionsPending ? "loading" : addresses.status;
    const base = isAddressesSettled ? addresses.data : null;

    // A "Pokaż więcej" that ended with nothing (failed, or cancelled by closing the panel) collapses back to its link, so
    // clicking it again retries; a new request for it clears the failure.
    const {failedKeys} = showMoreRequests;
    const expandedSections = useMemo(() => ui.expandedSections.filter((kind) => !failedKeys.includes(kind)), [ui.expandedSections, failedKeys]);
    const expandedNumberStreetKeys = useMemo(
        () => ui.expandedNumberStreetKeys.filter((streetKey) => !failedKeys.includes(streetKey)),
        [ui.expandedNumberStreetKeys, failedKeys]
    );

    const isDistrictsExpanded = expandedSections.includes("districts");
    const regionsResult = (isDistrictsExpanded ? showMoreRequests.expandedRegions : null) ?? regions.data;

    // Metrics are asked for the districts the list shows, which does not depend on the metrics themselves.
    const districtRegionIds = useMemo(() => {
        if (!parsed) {
            return [];
        }

        const districts = getPricesSearchDistricts(parsed, regionsResult, context);

        return (isDistrictsExpanded ? districts : districts.slice(0, PRICES_SEARCH_GROUP_LIMIT)).map((district) => district.regionId);
    }, [parsed, regionsResult, context, isDistrictsExpanded]);
    const metrics = usePricesSearchCityMetrics({isOpen, districtRegionIds});

    const {expandedStreetKey} = ui;
    const loadingShowMoreKeys = showMoreRequests.loadingKeys;
    const scoped = showMoreRequests.scoped;
    const baseModel = useMemo(
        () =>
            buildPricesSearchModel({
                parsed,
                status,
                base,
                scoped,
                regions: regionsResult,
                metrics,
                recent,
                nearby,
                ui: {expandedStreetKey, expandedSections, expandedNumberStreetKeys, loadingShowMoreKeys},
                context
            }),
        [
            parsed,
            status,
            base,
            scoped,
            regionsResult,
            metrics,
            recent,
            nearby,
            expandedStreetKey,
            expandedSections,
            expandedNumberStreetKeys,
            loadingShowMoreKeys,
            context
        ]
    );

    // Which rows are rendered does not depend on their prices, so the base model decides what to fetch.
    const pricedAddresses = useMemo(() => getPricesSearchPricedAddresses(baseModel), [baseModel]);
    const numberPrices = usePricesSearchNumberPrices(pricedAddresses);
    const model = useMemo(() => applyPricesSearchNumberPrices(baseModel, numberPrices), [baseModel, numberPrices]);

    // A number or an address still waiting for its price may turn out unavailable - autoActiveId then moves past it.
    const autoActiveItem =
        model.state === "results" && model.autoActiveId !== null ? model.navigation.find((item) => item.id === model.autoActiveId) : undefined;
    const isAutoActiveResolving =
        (autoActiveItem?.kind === "number" || autoActiveItem?.kind === "address") &&
        numberPrices[getPricesSearchNumberPriceKey(autoActiveItem.rcn)]?.isLoading === true;

    // The expanded street of a real (not stale) answer gets all its numbers when the answer was capped.
    const {loadStreetNumbers, loadCityStreets, loadDistricts, abortAll: abortShowMore} = showMoreRequests;
    const incompleteExpandedStreet = isAddressesSettled
        ? model.sections
              .flatMap((section) => section.items)
              .find((item): item is IPricesSearchStreetItem => item.kind === "street" && item.numbers?.isTotalExact === false)
        : undefined;
    useEffect(() => {
        if (incompleteExpandedStreet) {
            loadStreetNumbers(incompleteExpandedStreet, {silent: true});
        }
    }, [incompleteExpandedStreet?.streetKey, loadStreetNumbers]);

    // Closing the panel cancels the scoped requests too (the main ones stop through `isEnabled`), and a pending Enter.
    // The row an expansion was going to make active is forgotten too: reopening must not jump to it.
    useEffect(() => {
        if (!isOpen) {
            abortShowMore();
            setPendingSubmitKey(null);
            setActiveRequest(null);
        }
    }, [isOpen, abortShowMore]);

    const onPendingSubmitReadyRef = useRef(options.onPendingSubmitReady);
    useEffect(() => {
        onPendingSubmitReadyRef.current = options.onPendingSubmitReady;
    });

    // Enter during loading waits for this phrase only; a new phrase, an error or no results drop it.
    const isQuerySettled = isAddressesSettled && !isRegionsPending;
    useEffect(() => {
        if (pendingSubmitKey === null) {
            return;
        }
        if (pendingSubmitKey !== baseKey || addresses.status === "error") {
            setPendingSubmitKey(null);
            return;
        }
        // The auto-active row's price is still on its way: wait for it - the row may turn out unavailable.
        if (!isQuerySettled || isAutoActiveResolving) {
            return;
        }

        setPendingSubmitKey(null);
        if (model.state === "results" && model.autoActiveId !== null) {
            onPendingSubmitReadyRef.current(model.autoActiveId);
        }
    }, [pendingSubmitKey, baseKey, addresses.status, isQuerySettled, isAutoActiveResolving, model]);

    const validActiveRequest = activeRequest && activeRequest.baseKey === baseKey ? activeRequest : null;
    const requestedActiveId = validActiveRequest ? resolveActiveRequest(model, validActiveRequest) : null;
    // Nothing new appeared once the requests are done (e.g. a capped street had no more numbers): forget the request.
    useEffect(() => {
        if (activeRequest && (activeRequest.baseKey !== baseKey || (requestedActiveId === null && loadingShowMoreKeys.length === 0 && isQuerySettled))) {
            setActiveRequest(null);
        }
    }, [activeRequest, baseKey, requestedActiveId, loadingShowMoreKeys, isQuerySettled]);

    const consumeActiveRequest = useCallback(() => setActiveRequest(null), []);

    const toggleStreet = useCallback(
        (street: IPricesSearchStreetItem) => {
            if (baseKey === null) {
                return;
            }

            updateUi((previous) => ({...previous, expandedStreetKey: street.isExpanded ? null : street.streetKey}));
            setActiveRequest(street.isExpanded ? null : {baseKey, type: "numbers", streetKey: street.streetKey, index: 0});
        },
        [baseKey, updateUi]
    );

    const showMore = useCallback(
        (item: IPricesSearchShowMoreItem) => {
            if (parsed === null) {
                return;
            }

            if (item.target === "numbers") {
                const street = item.streetKey === undefined ? undefined : findStreet(model, item.streetKey);
                if (!street) {
                    return;
                }

                // A retry after a failure finds the key already there.
                updateUi((previous) =>
                    previous.expandedNumberStreetKeys.includes(street.streetKey)
                        ? previous
                        : {...previous, expandedNumberStreetKeys: [...previous.expandedNumberStreetKeys, street.streetKey]}
                );
                setActiveRequest({baseKey: parsed.baseKey, type: "numbers", streetKey: street.streetKey, index: PRICES_SEARCH_NUMBERS_PREVIEW});
                if (street.numbers?.isTotalExact === false) {
                    loadStreetNumbers(street);
                }
                return;
            }

            const kind = item.target;
            const shownCount = model.sections.find((section) => section.kind === kind)?.items.length ?? 0;
            updateUi((previous) =>
                previous.expandedSections.includes(kind) ? previous : {...previous, expandedSections: [...previous.expandedSections, kind]}
            );
            setActiveRequest({baseKey: parsed.baseKey, type: "section", kind, index: shownCount});

            if (kind === "districts") {
                loadDistricts(parsed.rcnQuery);
            }
            // A capped answer may hide more streets: ask again per city (the page's city, else the busiest ones).
            if ((kind === "streets" || kind === "addresses") && base?.capped) {
                const grouped = groupRcnAddresses({parsed, base, scoped, context});
                const cities = getShowMoreCitySlugs(grouped.streets, context).map((citySlug) => ({
                    citySlug,
                    query: PRICES_CITY_NAME_BY_SLUG.has(citySlug) ? getCityScopedQuery(parsed, PRICES_CITY_NAME_BY_SLUG.get(citySlug) as string) : null
                }));
                loadCityStreets(kind, cities);
            }
        },
        [parsed, model, base, scoped, context, updateUi, loadStreetNumbers, loadDistricts, loadCityStreets]
    );

    const {retry: retryAddresses, abort: abortAddresses} = addresses;
    const {retry: retryRegions, abort: abortRegions} = regions;
    const regionsStatus = regions.status;
    const retry = useCallback(() => {
        retryAddresses();
        if (regionsStatus === "error") {
            retryRegions();
        }
    }, [retryAddresses, retryRegions, regionsStatus]);

    const abortAll = useCallback(() => {
        abortAddresses();
        abortRegions();
        abortShowMore();
        setPendingSubmitKey(null);
    }, [abortAddresses, abortRegions, abortShowMore]);

    const markPendingSubmit = useCallback(() => setPendingSubmitKey(baseKey), [baseKey]);

    return {
        model,
        parsed,
        isPending: isPending(addresses.status) || isRegionsPending,
        isBusy: isPending(status),
        toggleStreet,
        showMore,
        requestedActiveId,
        consumeActiveRequest,
        retry,
        abortAll,
        markPendingSubmit,
        isAutoActiveResolving
    };
};
